import { Theme, alpha } from "@mui/material/styles";
import { SxProps } from "@mui/system";

export const tableContainerStyles = {
  /* Scroll horizontal habilitado — essencial para mobile */
  overflowX: "auto",
  overflowY: "visible",
  /* Bordas arredondadas e sombra sutil — aspecto de card moderno */
  borderRadius: 2,
  border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  width: "100%",
  /* Esconde scrollbar no mobile mantendo scroll funcional */
  scrollbarWidth: "thin" as const,
  "&::-webkit-scrollbar": { height: 4 },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 4,
    backgroundColor: (theme: Theme) => theme.palette.divider,
  },
  "& .MuiTableCell-head": {
    color: "text.primary",
    fontSize: "0.8125rem",
    letterSpacing: "0.02em",
  },
} satisfies SxProps<Theme>;

export const tableHeaderCellStyles = {
  fontWeight: 700,
  /* Fundo do header: tom primário bem suave para se destacar das linhas */
  backgroundColor: (theme: Theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.primary.main + "0D", // 5 % de opacidade
  borderBottom: (theme: Theme) =>
    `2px solid ${theme.palette.primary.main}`,
  py: 1.5,
  px: 1.5,
  color: "text.primary",
  /* textAlign não é fixado aqui — cada célula define o seu via column.align */
  textTransform: "uppercase" as const,
  fontSize: "0.72rem",
  letterSpacing: "0.06em",
  verticalAlign: "middle",
} satisfies SxProps<Theme>;

export const tableRowStyles = {
  transition: "background-color 0.14s ease",
  /* Zebra striping leve via nth-child */
  "&:nth-of-type(even)": {
    backgroundColor: (theme: Theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.025)"
        : "rgba(0,0,0,0.018)",
  },
  /* Hover base — sobrescrito por linha no GenericTableBody quando necessário */
  "&:hover": {
    backgroundColor: (theme: Theme) =>
      alpha(theme.palette.primary.main, 0.03),
  },
  /* Borda inferior continua nas células */
  "& td": {
    borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  },
  "&:last-child td": {
    borderBottom: 0,
  },
} satisfies SxProps<Theme>;

export const tableDataCellStyles = {
  py: 0.75,
  px: 1.5,
  /* textAlign não é fixado aqui — cada célula define via column.align */
  fontSize: "0.8125rem",
  color: "text.primary",
  verticalAlign: "middle",
  /* Necessário para que o overflow/clamp dos filhos funcione em table cells */
  overflow: "hidden",
  /* maxWidth: 0 força a célula a respeitar a largura atribuída pelo tableLayout */
  maxWidth: 0,
} satisfies SxProps<Theme>;

export const tableActionCellStyles = {
  ...tableDataCellStyles,
  /**
   * Coluna de ações: sem largura fixa em px para não quebrar o tableLayout=fixed.
   * O espaço é o que sobrar após as colunas de dados (configuradas com %).
   */
  minWidth: 72,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
} satisfies SxProps<Theme>;

export const tableActionContentStyles = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  /**
   * Legado: vários componentes `*Actions.tsx` retornam um `<TableCell>`.
   * Neutralizamos bordas/padding do TableCell interno para evitar `td` aninhado.
   */
  "& .MuiTableCell-root": {
    borderBottom: "none !important",
    padding: 0,
    backgroundColor: "transparent",
  },
} satisfies SxProps<Theme>;

export const tableActionHeaderCellStyles = {
  ...tableHeaderCellStyles,
  minWidth: 72,
} satisfies SxProps<Theme>;

export const tablePaginationStyles = {
  borderTop: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  ".MuiTablePagination-select": {
    marginRight: 1,
  },
  ".MuiTablePagination-selectLabel": {
    marginRight: 1,
  },
  ".MuiTablePagination-toolbar": {
    minHeight: 48,
  },
} satisfies SxProps<Theme>;
